import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, catchError, Observable } from "rxjs";

import { environment } from "../../../../environments/environment";
import { handleError } from "../../helper/http-helpers";
import { CertifyDocument } from "../../models/certify-document.model";
import { DeleteModuleDocument } from "../../models/deleteModuleDocument.model";
import { GenericResponseDto } from "../../models/genericResponseDto.model";
import { UploadContentDto } from "../../models/upload-content-dto.model";
import { UploadContentResponseDto } from "../../models/upload-content-response-dto.model";

@Injectable({
    providedIn: "root",
})
export class DocumentService {
    private baseUrl = environment.apiBaseUrl;
    private documentRelativeUrl = `/compliance`;
    private documentUrl = `${this.baseUrl}/document`;

    /** Signal to indicate if all documents are certified */
    private allCertifiedSubject$ = new BehaviorSubject<boolean>(false);
    public allCertified$ = this.allCertifiedSubject$.asObservable();

    constructor(private http: HttpClient) {}
    /** Call this method to update the signal value */
    updateAllCertifiedStatus(documents: any[], selectedYear?: number): void {
        const currentYear = new Date().getFullYear();
        const year = selectedYear ?? currentYear;

        // Filter out non-mandatory documents (typeId === 2 or typeId === 14)
        const mandatoryDocuments = documents.filter((doc) => doc.typeId !== 2 && doc.typeId !== 14);

        // Check if all mandatory documents meet certification requirements
        const allCertified = mandatoryDocuments.length > 0
            && mandatoryDocuments.every((doc) => {
                // Document must be uploaded
                if (!doc.documentId) {
                    return false;
                }

                // If hasChanges is true, need both signatures before certification
                if (doc.hasChanges === true) {
                    return !!doc.certified;
                }

                // For year 2025 and below, just check if certified
                if (year <= 2025) {
                    return !!doc.certified;
                }

                // For year > 2025, apply special rules for document types 5, 8, 11
                if ([5, 8, 11].includes(doc.typeId)) {
                    // If hasChanges is false, only vendor signature needed, can be certified
                    if (doc.hasChanges === false && doc.vendorSign === true) {
                        return !!doc.certified;
                    }
                }

                // For other document types in year > 2025, require both signatures
                return doc.reviewerSign === true && doc.vendorSign === true && !!doc.certified;
            });

        this.allCertifiedSubject$.next(allCertified);
    }

    downloadFile(url: string) {
        return this.http
            .get(`${url}`, {
                responseType: "blob",
            })
            .pipe(catchError(handleError));
    }

    certify(data: CertifyDocument):
    Observable<GenericResponseDto<CertifyDocument>> {
        return this.http.post<GenericResponseDto<CertifyDocument>>(
            `${this.documentUrl}/Certify`,
            data,
        );
    }

    deleteDocument(
        documentId: string
    ): Observable<GenericResponseDto<any>> {
        return this.http
            .get<
        GenericResponseDto<any>
        >(`${this.documentUrl}/Delete?documentId=${documentId}`)
            .pipe(catchError(handleError));
    }

    deleteDocument2(data: DeleteModuleDocument):
    Observable<GenericResponseDto<string>> {
        return this.http.post<GenericResponseDto<string>>(
            `${this.documentUrl}/DeleteDocument`,
            data,
        );
    }

    uploadContent(data: UploadContentDto): Observable<GenericResponseDto<UploadContentResponseDto>> {
        return this.http.post<GenericResponseDto<UploadContentResponseDto>>(
            `${this.documentUrl}/UploadContent`,
            data
        ).pipe(catchError(handleError));
    }

    downloadTemplateBunch(): Observable<Blob> {
        return this.http.get(`${this.documentUrl}/DownloadTemplateBunch`, {
            responseType: "blob"
        }).pipe(catchError(handleError));
    }
}
